import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import SEO from '../components/SEO';
import { authAPI } from '../utils/api';
import LanguageSelector from '../components/LanguageSelector';
import { Mail, RefreshCw, Shield } from 'lucide-react';

const LoginPage = ({ setUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showVerificationNeeded, setShowVerificationNeeded] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showTroubleSigningIn, setShowTroubleSigningIn] = useState(false);
  // 2FA State
  const [show2FAInput, setShow2FAInput] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [twoFAEmail, setTwoFAEmail] = useState('');

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      toast.error('Please enter your email address first');
      return;
    }
    
    setResendingVerification(true);
    try {
      await authAPI.resendVerification({ email: formData.email });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else {
        toast.error('Failed to send verification email. Please try again.');
      }
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowVerificationNeeded(false);
    
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification');
      return;
    }
    
    setLoading(true);

    try {
      const response = await authAPI.login({
        ...formData,
        recaptcha_token: recaptchaToken,
        mfa_code: show2FAInput ? mfaCode : undefined
      });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setShow2FAInput(true);
        setTwoFAEmail(response.data.email);
        toast.info('A verification code has been sent to your email.');
        setLoading(false);
        return;
      }
      
      const { token, user } = response.data;

      localStorage.setItem('movemate_token', token);
      localStorage.setItem('movemate_user', JSON.stringify(user));
      setUser(user);

      toast.success('Welcome back!');
      
      // Redirect based on role - mobile-only roles should always go to mobile dashboard
      const mobileOnlyRoles = ['driver_crew', 'porter', 'customer', 'customer_commercial'];
      if (mobileOnlyRoles.includes(user.role)) {
        navigate('/mobile/dashboard');
      } else if (user.role_level >= 20 || user.access_type === 'panel_only' || user.access_type === 'both') {
        navigate('/control-panel');
      } else {
        navigate('/mobile/dashboard');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      
      // Check if it's an email verification error
      if (errorMessage.toLowerCase().includes('verify your email') || error.response?.status === 403) {
        setShowVerificationNeeded(true);
      }
      
      // Check if 2FA code was wrong - don't reset the 2FA form
      if (errorMessage.toLowerCase().includes('2fa') || errorMessage.toLowerCase().includes('verification code')) {
        setMfaCode(''); // Clear the code input but keep 2FA form visible
      } else {
        // Reset 2FA state on other errors
        setShow2FAInput(false);
        setMfaCode('');
      }
      
      toast.error(errorMessage);
      // Reset reCAPTCHA on error
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      {/* Language Selector in top right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="compact" />
      </div>
      
      <SEO 
        title="Login - Company Portal"
        description="Sign in to your MoveMate company portal. Manage jobs, inventory, customers and team from your dashboard."
        url="https://admin.movematebend.co.uk/login"
      />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="MoveMate" className="h-14" />
            <h1 className="text-3xl font-bold gradient-text">MoveMate</h1>
          </div>
        </div>

        <Card className="glassmorphism border-2 border-white/50 shadow-xl" data-testid="login-card">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-3xl font-bold gradient-text">{t('welcome_back')}</CardTitle>
            <CardDescription className="text-base mt-2">{t('sign_in_continue')}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Email Verification Needed Alert */}
            {showVerificationNeeded && (
              <Alert className="mb-6 bg-amber-50 border-amber-200">
                <Mail className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <p className="font-medium mb-2">Email verification required</p>
                  <p className="text-sm mb-3">Please check your inbox and click the verification link before logging in.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    className="border-amber-300 hover:bg-amber-100"
                  >
                    {resendingVerification ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* 2FA Code Input */}
              {show2FAInput ? (
                <div className="space-y-4">
                  <Alert className="bg-blue-50 border-blue-200">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <p className="font-medium mb-1">Two-Factor Authentication</p>
                      <p className="text-sm">A 6-digit code has been sent to <strong>{twoFAEmail}</strong></p>
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-2">
                    <Label htmlFor="mfa_code" className="text-base">Verification Code</Label>
                    <Input
                      id="mfa_code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      className="h-12 text-base text-center tracking-widest text-xl"
                      maxLength={6}
                      data-testid="mfa-code-input"
                      autoFocus
                    />
                  </div>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShow2FAInput(false);
                      setMfaCode('');
                      recaptchaRef.current?.reset();
                      setRecaptchaToken(null);
                    }}
                    className="w-full text-gray-500"
                  >
                    ← Back to login
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base">{t('email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12 text-base"
                      data-testid="login-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="password" className="text-base">{t('password')}</Label>
                      <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                        {t('auth.forgot_password')}
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="h-12 text-base"
                      data-testid="login-password-input"
                    />
                  </div>
                </>
              )}

              {/* reCAPTCHA - only show when not in 2FA mode */}
              {!show2FAInput && (
                <div className="flex justify-center" data-testid="recaptcha-container">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={loading || (!show2FAInput && !recaptchaToken) || (show2FAInput && mfaCode.length !== 6)}
                data-testid="login-submit-btn"
              >
                {loading ? t('loading') : (show2FAInput ? 'Verify Code' : t('login'))}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              {/* Trouble signing in link */}
              <button
                type="button"
                onClick={() => setShowTroubleSigningIn(!showTroubleSigningIn)}
                className="text-sm text-gray-500 hover:text-primary hover:underline cursor-pointer"
                data-testid="trouble-signing-in-link"
              >
                Trouble signing in?
              </button>
              
              {/* Expanded trouble signing in options */}
              {showTroubleSigningIn && (
                <div className="bg-gray-50 rounded-lg p-4 text-left space-y-3">
                  <p className="text-sm text-gray-600 font-medium">Common issues:</p>
                  
                  {/* Resend Verification Option */}
                  <div className="flex items-start gap-3 p-3 bg-white rounded border">
                    <Mail className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Haven't verified your email?</p>
                      <p className="text-xs text-gray-500 mb-2">Enter your email above, then click resend.</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResendVerification}
                        disabled={resendingVerification || !formData.email}
                        className="text-xs"
                        data-testid="resend-verification-btn"
                      >
                        {resendingVerification ? (
                          <>
                            <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-1 h-3 w-3" />
                            Resend Verification Email
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Forgot Password Option */}
                  <div className="flex items-start gap-3 p-3 bg-white rounded border">
                    <RefreshCw className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Forgot your password?</p>
                      <p className="text-xs text-gray-500 mb-2">Reset it via email link.</p>
                      <Link to="/forgot-password">
                        <Button type="button" variant="outline" size="sm" className="text-xs">
                          Reset Password
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-sm text-gray-600">
                {t('dont_have_account')}{' '}
                <Link to="/register-company" className="text-primary font-semibold hover:underline" data-testid="login-register-link">
                  {t('register')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
